import {
  isLoomAcceptedMeshSchema,
  type LoomAcceptedMeshHeader,
  type LoomProtectedFeatureChain,
} from "./loomAcceptedMeshContract";
import {
  isVerifiedLoomAcceptedMesh,
  type LoomVerifiedAcceptedMeshContract,
} from "./loomAcceptedMeshIntegrity";
import {
  decodeLoomAcceptedUnionReceipt,
  type LoomDecodedUnionReceipt,
} from "./loomAcceptedUnionDecoder";

export interface LoomAcceptedMeshContract {
  header: LoomAcceptedMeshHeader;
  positions: Float32Array;
  faces: Uint32Array;
  facePatchIndices: Uint32Array;
  faceSourceNodeIndices: Uint32Array;
  faceCornerParameters: Float32Array;
  protectedFeatures: {
    edges: Uint32Array;
    adjacentFaces: Uint32Array;
    chainEdgeIndices: Uint32Array;
  };
  unionReceipt?: LoomDecodedUnionReceipt;
}

export interface LoomAcceptedMeshArrayReaders {
  f32: (name: string) => Float32Array;
  u32: (name: string) => Uint32Array;
}

export interface LoomPanelizeTrisAcceptedInput {
  positions: Float32Array;
  faces: Uint32Array;
  protectedTrailingEdges: {
    edges: Uint32Array;
    upperFaces: Uint32Array;
    lowerFaces: Uint32Array;
    chainIds: readonly string[];
  };
  geometryRevision: string;
  topologyRevision: string;
}

export interface LoomLegacyPanelFeatureArrays {
  trailingEdgeEdges: Uint32Array;
  trailingEdgeUpperFaces: Uint32Array;
  trailingEdgeLowerFaces: Uint32Array;
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function faceVertices(faces: Uint32Array, face: number): readonly [number, number, number] {
  return [faces[3 * face]!, faces[3 * face + 1]!, faces[3 * face + 2]!];
}

function acceptedFaceIncidence(
  faces: Uint32Array,
  vertexCount: number,
): Map<string, number[]> {
  const incidence = new Map<string, number[]>();
  for (let face = 0; face < faces.length / 3; face += 1) {
    const vertices = faceVertices(faces, face);
    if (new Set(vertices).size !== 3 || vertices.some((vertex) => vertex >= vertexCount)) {
      throw new Error("acceptedGeometry contains an invalid accepted face");
    }
    for (const [a, b] of [[vertices[0], vertices[1]], [vertices[1], vertices[2]], [vertices[2], vertices[0]]] as const) {
      const key = edgeKey(a, b);
      incidence.set(key, [...(incidence.get(key) ?? []), face]);
    }
  }
  return incidence;
}

function validateOwnership(
  header: LoomAcceptedMeshHeader,
  facePatchIndices: Uint32Array,
  faceSourceNodeIndices: Uint32Array,
): void {
  const patchUse = new Uint32Array(header.patches.length);
  for (let face = 0; face < facePatchIndices.length; face += 1) {
    const patchIndex = facePatchIndices[face]!;
    const sourceNodeIndex = faceSourceNodeIndices[face]!;
    if (patchIndex >= header.patches.length || sourceNodeIndex >= header.sourceNodes.length) {
      throw new Error("acceptedGeometry face ownership contains an out-of-range index");
    }
    if (header.patches[patchIndex]!.sourceNodeIndex !== sourceNodeIndex) {
      throw new Error("acceptedGeometry face patch/source-node ownership disagrees");
    }
    patchUse[patchIndex] = patchUse[patchIndex]! + 1;
  }
  if (patchUse.some((count) => count === 0)) throw new Error("acceptedGeometry contains an unowned patch definition");
}

function chainEdges(
  chain: LoomProtectedFeatureChain,
  chainEdgeIndices: Uint32Array,
  edges: Uint32Array,
): readonly [number, number][] {
  const result: Array<[number, number]> = [];
  for (let offset = chain.edgeOffset; offset < chain.edgeOffset + chain.edgeCount; offset += 1) {
    const edgeIndex = chainEdgeIndices[offset]!;
    if (edgeIndex >= edges.length / 2) throw new Error("acceptedGeometry protected chain references an unknown edge");
    result.push([edges[2 * edgeIndex]!, edges[2 * edgeIndex + 1]!]);
  }
  for (let index = 1; index < result.length; index += 1) {
    if (result[index - 1]![1] !== result[index]![0]) {
      throw new Error(`acceptedGeometry protected chain ${chain.chainId} is not ordered and continuous`);
    }
  }
  const closes = result[result.length - 1]![1] === result[0]![0];
  if (closes !== chain.closed) throw new Error(`acceptedGeometry protected chain ${chain.chainId} closure is inconsistent`);
  return result;
}

function validateProtectedFeatures(
  header: LoomAcceptedMeshHeader,
  faces: Uint32Array,
  facePatchIndices: Uint32Array,
  faceSourceNodeIndices: Uint32Array,
  faceCornerParameters: Float32Array,
  edges: Uint32Array,
  adjacentFaces: Uint32Array,
  chainEdgeIndices: Uint32Array,
  vertexCount: number,
): void {
  const faceCount = faces.length / 3;
  const incidence = acceptedFaceIncidence(faces, vertexCount);
  for (let edge = 0; edge < edges.length / 2; edge += 1) {
    const a = edges[2 * edge]!; const b = edges[2 * edge + 1]!;
    if (a >= vertexCount || b >= vertexCount || a === b) throw new Error("acceptedGeometry protected edge is invalid");
    const actual = incidence.get(edgeKey(a, b)) ?? [];
    const claimed: [number, number] = [adjacentFaces[2 * edge]!, adjacentFaces[2 * edge + 1]!];
    if (actual.length !== 2 || claimed[0] >= faceCount || claimed[1] >= faceCount || claimed[0] === claimed[1]
        || !actual.includes(claimed[0]) || !actual.includes(claimed[1])) {
      throw new Error("acceptedGeometry protected edge is not the claimed two-sided accepted face edge");
    }
  }
  const referenced = new Set<number>();
  for (const chain of header.protectedFeatures.chains) {
    chainEdges(chain, chainEdgeIndices, edges);
    for (let offset = chain.edgeOffset; offset < chain.edgeOffset + chain.edgeCount; offset += 1) {
      const edgeIndex = chainEdgeIndices[offset]!;
      referenced.add(edgeIndex);
      const upperOrFirst = adjacentFaces[2 * edgeIndex]!;
      const lowerOrSecond = adjacentFaces[2 * edgeIndex + 1]!;
      const patchPair: [number, number] = [facePatchIndices[upperOrFirst]!, facePatchIndices[lowerOrSecond]!];
      const sourcePair: [number, number] = [faceSourceNodeIndices[upperOrFirst]!, faceSourceNodeIndices[lowerOrSecond]!];
      if (patchPair.some((item) => !chain.patchIndices.includes(item))
          || sourcePair.some((item) => !chain.sourceNodeIndices.includes(item))) {
        throw new Error(`acceptedGeometry protected chain ${chain.chainId} provenance disagrees with its adjacent faces`);
      }
      if (chain.kind === "trailing_edge" && patchPair[0] === patchPair[1]) {
        throw new Error("acceptedGeometry Kutta edge does not have distinct upper/lower patch ownership");
      }
      if (chain.kind === "trailing_edge") {
        const upper = header.patches[patchPair[0]]!; const lower = header.patches[patchPair[1]]!;
        if (upper.surfaceRole !== "upper" || lower.surfaceRole !== "lower"
            || upper.evaluatorKind !== "derived-feature" || lower.evaluatorKind !== "derived-feature") {
          throw new Error("acceptedGeometry Kutta adjacent-face ordering or derived evaluator provenance is invalid");
        }
        for (const vertex of [edges[2 * edgeIndex]!, edges[2 * edgeIndex + 1]!] as const) {
          const upperCorner = faceVertices(faces, upperOrFirst).indexOf(vertex);
          const lowerCorner = faceVertices(faces, lowerOrSecond).indexOf(vertex);
          if (upperCorner < 0 || lowerCorner < 0) {
            throw new Error("acceptedGeometry Kutta edge is absent from its ordered adjacent face");
          }
          const upperU = faceCornerParameters[(upperOrFirst * 3 + upperCorner) * 2]!;
          const lowerU = faceCornerParameters[(lowerOrSecond * 3 + lowerCorner) * 2]!;
          if (Math.abs(upperU) > 1e-5 || Math.abs(lowerU - 0.5) > 1e-5) {
            throw new Error("acceptedGeometry Kutta order is not physical upper u=0 then lower u=0.5");
          }
        }
      }
      if (chain.kind === "boolean_shared_seam" && sourcePair[0] === sourcePair[1]) {
        throw new Error("acceptedGeometry Boolean seam does not separate source-node ownership");
      }
    }
  }
  if (referenced.size !== edges.length / 2) throw new Error("acceptedGeometry has protected edges outside all typed chains");
}

export function decodeLoomAcceptedMeshContract(
  header: LoomAcceptedMeshHeader,
  readers: LoomAcceptedMeshArrayReaders,
  exactSurface?: { facesArray: string; vertexCount: number },
): LoomAcceptedMeshContract {
  const positions = readers.f32(header.positionsArray);
  const faces = readers.u32(header.facesArray);
  const facePatchIndices = readers.u32(header.facePatchIndicesArray);
  const faceSourceNodeIndices = readers.u32(header.faceSourceNodeIndicesArray);
  const faceCornerParameters = readers.f32(header.faceCornerParameters.array);
  const edges = readers.u32(header.protectedFeatures.edgesArray);
  const adjacentFaces = readers.u32(header.protectedFeatures.adjacentFacesArray);
  const chainEdgeIndices = readers.u32(header.protectedFeatures.chainEdgeIndicesArray);
  if (!positions.every(Number.isFinite) || !faceCornerParameters.every(Number.isFinite)) {
    throw new Error("acceptedGeometry contains non-finite geometry or face-corner parameters");
  }
  validateOwnership(header, facePatchIndices, faceSourceNodeIndices);
  validateProtectedFeatures(
    header, faces, facePatchIndices, faceSourceNodeIndices,
    faceCornerParameters, edges, adjacentFaces, chainEdgeIndices, positions.length / 3,
  );
  if (header.unionReceipt && !exactSurface) {
    throw new Error("v3 union receipt has no exact-surface decode context");
  }
  const unionReceipt = exactSurface ? decodeLoomAcceptedUnionReceipt(
    header, readers.u32(exactSurface.facesArray), exactSurface.vertexCount, readers,
  ) : undefined;
  return {
    header,
    positions,
    faces,
    facePatchIndices,
    faceSourceNodeIndices,
    faceCornerParameters,
    protectedFeatures: { edges, adjacentFaces, chainEdgeIndices },
    ...(unionReceipt ? { unionReceipt } : {}),
  };
}

function protectedTrailingEdges(accepted: LoomAcceptedMeshContract): {
  edges: Uint32Array;
  upperFaces: Uint32Array;
  lowerFaces: Uint32Array;
  chainIds: readonly string[];
} {
  const edges: number[] = []; const upperFaces: number[] = []; const lowerFaces: number[] = [];
  const chainIds: string[] = [];
  for (const chain of accepted.header.protectedFeatures.chains) {
    if (chain.kind !== "trailing_edge" || !chain.kutta || chain.adjacentFaceOrdering !== "upper-lower") continue;
    chainIds.push(chain.chainId);
    for (let offset = chain.edgeOffset; offset < chain.edgeOffset + chain.edgeCount; offset += 1) {
      const edgeIndex = accepted.protectedFeatures.chainEdgeIndices[offset]!;
      edges.push(accepted.protectedFeatures.edges[2 * edgeIndex]!, accepted.protectedFeatures.edges[2 * edgeIndex + 1]!);
      upperFaces.push(accepted.protectedFeatures.adjacentFaces[2 * edgeIndex]!);
      lowerFaces.push(accepted.protectedFeatures.adjacentFaces[2 * edgeIndex + 1]!);
    }
  }
  if (edges.length === 0) throw new Error("Accepted geometry has no protected Kutta trailing edge for panelize_tris");
  return {
    edges: new Uint32Array(edges),
    upperFaces: new Uint32Array(upperFaces),
    lowerFaces: new Uint32Array(lowerFaces),
    chainIds,
  };
}

function equalUint32(left: Uint32Array, right: Uint32Array): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

export function assertAcceptedGeometryMatchesLegacyPanel(
  accepted: LoomAcceptedMeshContract,
  legacy: LoomLegacyPanelFeatureArrays,
): void {
  const protectedEdges = protectedTrailingEdges(accepted);
  if (!equalUint32(protectedEdges.edges, legacy.trailingEdgeEdges)
      || protectedEdges.upperFaces.length !== legacy.trailingEdgeUpperFaces.length
      || protectedEdges.lowerFaces.length !== legacy.trailingEdgeLowerFaces.length) {
    throw new Error("acceptedGeometry protected Kutta chain disagrees with legacy panel arrays");
  }
  for (let edge = 0; edge < protectedEdges.upperFaces.length; edge += 1) {
    const acceptedUpper = protectedEdges.upperFaces[edge]!;
    const acceptedLower = protectedEdges.lowerFaces[edge]!;
    const legacyFirst = legacy.trailingEdgeUpperFaces[edge]!;
    const legacySecond = legacy.trailingEdgeLowerFaces[edge]!;
    if (!((acceptedUpper === legacyFirst && acceptedLower === legacySecond)
        || (acceptedUpper === legacySecond && acceptedLower === legacyFirst))) {
      throw new Error("acceptedGeometry physical Kutta face pair disagrees with legacy panel incidence");
    }
  }
}

export function acceptedGeometryForPanelizeTris(
  asset: { acceptedGeometry: LoomVerifiedAcceptedMeshContract },
): LoomPanelizeTrisAcceptedInput {
  const accepted = asset.acceptedGeometry;
  const panelization = accepted.header.receipts.panelization;
  const meshReceipt = accepted.header.receipts.mesh;
  if (!isLoomAcceptedMeshSchema(accepted.header.schema) || accepted.header.status !== "accepted"
      || panelization.status !== "current" || panelization.staleReason !== null
      || meshReceipt.positionsArray !== accepted.header.positionsArray
      || meshReceipt.facesArray !== accepted.header.facesArray
      || meshReceipt.geometryRevision !== accepted.header.geometryRevision
      || meshReceipt.topologyRevision !== accepted.header.topologyRevision
      || panelization.outputPositionsArray !== accepted.header.positionsArray
      || panelization.outputFacesArray !== accepted.header.facesArray
      || panelization.outputGeometryRevision !== accepted.header.geometryRevision
      || panelization.outputTopologyRevision !== accepted.header.topologyRevision
      || !isVerifiedLoomAcceptedMesh(accepted)) {
    throw new Error("Accepted geometry is unverified, provisional, or stale and cannot be passed to panelize_tris");
  }
  const trailingEdges = protectedTrailingEdges(accepted);
  return {
    positions: accepted.positions,
    faces: accepted.faces,
    protectedTrailingEdges: trailingEdges,
    geometryRevision: accepted.header.geometryRevision,
    topologyRevision: accepted.header.topologyRevision,
  };
}
